import { FC, useEffect, useState } from "react"

import { Card, Row, Typography } from "antd"
import { CartesianGrid, Legend, Line, LineChart, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts"

import { Table } from "@/components/_Shared/Table"
import { AnalyticsAreaChart } from "@/components/Analytics/AnalyticsAreaChart"
import { AnalyticsBarChart } from "@/components/Analytics/AnalyticsBarChart"
import { AnalyticsPieChart } from "@/components/Analytics/AnalyticsPieChart"
import {
  useCustomerAnalyticsHeader
} from "@/components/Analytics/AnalyticsSettings/CustomerAnalytics/useCustomerAnalyticsHeader"
import { useAnalytics, ITotalNumberAnalytics } from "@/components/Analytics/useAnalytics"
import { Resource } from "@/constants/permission"
import { getAnalyticsRefusalExcel } from "@/http/analytics"
import classes from "@/pages/Analytics/Common/Analytics.module.scss"
import AnalyticsHeader from "@/pages/Analytics/Common/AnalyticsHeader"
import AnalyticsTitle from "@/pages/Analytics/Common/AnalyticsTitle"

import { orderRefusalColumns } from "./AnalyticsRefusalPageColumns"

import { useRefusalsAnalyticsQuery } from "@/hook/Analytics/useAnalyticsQuery"
import { useDownloadFile } from "@/hook/useDownloadFile"
import { useTable } from "@/hook/useTable"

import { IAnalyticsPageDisplayUnit, IRefusalAnalytics } from "@/types/IAnalytics"
import { IDictionary, ITableColumn } from "@/types/ui"

const { Title } = Typography

const displayUnits = [
  {
    value: "refusalCount",
    label: "Количество",
    bigLabel: "Количество отказов"
  },
  {
    value: "refusalAmount",
    label: "Тонны",
    bigLabel: "Объем отказов (Тонны)"
  }
]

const additionalDataKeys: IDictionary<IAnalyticsPageDisplayUnit> = {
  "refusalCount": {
    value: "orderCount",
    label: "",
    bigLabel: "Количество заказов"
  },
  "refusalAmount": {
    value: "orderAmount",
    label: "",
    bigLabel: "Объем заказов (Тонны)"
  },
  "deliverySum": {
    value: "pickupSum",
    label: "",
    bigLabel: "Сумма самовывозов"
  },
  "deliverySumMillions": {
    value: "pickupSumMillions",
    label: "",
    bigLabel: "Сумма самовывозов (млн)"
  }
}

const AnalyticsRefusalPage: FC = () => {
  const { optionsList, component: HeaderComponent, isComparison } = useCustomerAnalyticsHeader({
    baseDisplayUnits: [],
    additionalDisplayUnits: displayUnits
  })

  const [initialColumns, setInitialColumns] =
    useState<ITableColumn<IRefusalAnalytics>[]>(orderRefusalColumns(true))

  const [analyticsMapped, setAnalyticsMapped] = useState<IRefusalAnalytics[]>([])
  const [displayUnit, setDisplayUnit] = useState<IAnalyticsPageDisplayUnit>(displayUnits[0])


  const additionalDataKey = additionalDataKeys[displayUnit.value]

  const { columns, pagination, filter, order } = useTable({
    initialColumns: initialColumns
  })

  useEffect(() => {
    setInitialColumns(orderRefusalColumns(!isComparison))
  }, [isComparison])

  const { data: analyticsRefusalData, isLoading: isLoadingAnalyticsRefusal } = useRefusalsAnalyticsQuery({
      optionsList
    },
    {
      Page: pagination.page,
      PageSize: pagination.pageSize,
      Filter: filter.filter,
      OrderBy: order.orderBy
    })

  useEffect(() => {
    if (analyticsRefusalData?.response.data) {
      setAnalyticsMapped(analyticsRefusalData.response.data.map(r => ({
        rowKey: crypto.randomUUID(),
        period: r.period,
        refusalCount: r.refusalCount,
        refusalAmount: r.refusalAmount,
        orderCount: r.orderCount,
        orderAmount: r.orderAmount
      })))
    }
  }, [analyticsRefusalData])

  useEffect(() => {      
    if (analyticsMapped && analyticsMapped.length > 0) {
      setTotalNumberAnalytics(getTotalNumberAnalytics(analyticsMapped))
    }
  }, [analyticsMapped])


  const forOnlyRefusalDiagramData = analyticsRefusalData?.response.data.filter(r => r.refusalCount > 0)
  const { getTotalNumberAnalytics } = useAnalytics({})
  const [totalNumberAnalytics, setTotalNumberAnalytics] = useState<ITotalNumberAnalytics[]>([])
  const mainColor = "#8884d8"
  const secondColor = "#387908"

  const { download: excelDownload, isLoading: isExcelLoading } =
    useDownloadFile(() =>
      getAnalyticsRefusalExcel({
          optionsList
        },
        columns.data,
        {
          Page: pagination.page,
          PageSize: pagination.pageSize,
          Filter: filter.filter,
          OrderBy: order.orderBy
        })
    )

  return (
    <div>
      <AnalyticsTitle 
        text={"Динамика отказов"}
      />
      <AnalyticsHeader 
        displayUnits={displayUnits}
        activeUnit={displayUnit}
        component={HeaderComponent}
        onChangeTabs={setDisplayUnit}
      />
      <Card bordered={false} className={ classes.cardWithTabs }>
        <Title level={4}>Отказы за период</Title>
        <Row>
          {
            !isComparison &&
              <AnalyticsAreaChart dataKeys={[displayUnit]} data={forOnlyRefusalDiagramData}/>
          }
          {
            isComparison &&
              <AnalyticsBarChart
                  dataKeys={[displayUnit]}
                  data={forOnlyRefusalDiagramData}/>
          }
        </Row>

        <Title level={4}>Отказы в сравнении с количеством заказов за период</Title>
        <Row>
          {
            !isComparison &&
              <LineChart
                  data={analyticsMapped}
                  width={1400}
                  height={400}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <XAxis dataKey="period"/>
                  <YAxis domain={["dataMin", "dataMax"]} />
                  <ChartTooltip/>
                  <Legend wrapperStyle={{ width: "100%" }}/>
                  <CartesianGrid stroke="#f5f5f5"/>
                  <Line type="monotone" name={displayUnit.bigLabel}
                        dataKey={displayUnit.value}
                        stroke={secondColor}/>
                  <Line type="monotone" name={additionalDataKey.bigLabel}
                        dataKey={additionalDataKey.value}/>
              </LineChart>
          }
          {
            isComparison &&
              <AnalyticsBarChart
                  dataKeys={[displayUnit, additionalDataKey]}
                  data={analyticsMapped}/>
          }
        </Row>

        <Title level={4}>Общее количество заказов и отказов</Title>
        <Row>
          <AnalyticsPieChart
            chartWidth={730}
            chartHeight={400}
            dataKey={"count"}
            name={"totalNumberAnalytics"}
            data={[{ count: totalNumberAnalytics.find(x => x.key === displayUnit.value)?.value }, 
              { count: totalNumberAnalytics.find(x => x.key === additionalDataKey.value)?.value }]}
            nameKey="totalNumberAnalytics"
            fill={mainColor}
            fillCells={[secondColor, mainColor]}/>
        </Row>

        <Table
          dataSource={analyticsMapped}
          columns={columns.data}
          download={{
            onDownload: excelDownload,
            isLoading: isExcelLoading
          }}
          isSelectable={false}
          rowKey="rowKey"
          isLoading={isLoadingAnalyticsRefusal}
          onUpdateColumns={columns.onColumnChange}
          pagination={{
            currentPage: pagination.page,
            pageSize: pagination.pageSize,
            total: analyticsRefusalData?.response.count,
            onPageChange: pagination.onPageChange,
            onPageSizeChange: pagination.onPageSizeChange
          }}
          onSorterChange={order.setOrderBy}
          permission={{
            resource: Resource.RefusalsAnalytics
          }}
        />
      </Card>
    </div>
  )
}

export default AnalyticsRefusalPage